import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import * as htmlToText from 'html-to-text';

import { getValueWithEllipsis } from './strings';

// Helper to escape regex special characters in the query
function escapeRegExp(string) {
  return String(string).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Helper to validate query string for highlight
function isValidQuery(query) {
  // Must be a non-empty string, no control characters, no surrogate pairs, no null bytes
  return (
    typeof query === 'string' &&
    !!query.trim() &&
    !/[\u0000-\u001F\u007F]/.test(query)
  );
}

export const sanitizeHtml = (srcHtml, options = {}) => {
  if (!srcHtml) return '';
  // Add target blank to all links
  const formattedHtml = srcHtml.replace(
    /<a\s+(?![^>]*\btarget\s*=\s*(['"])[^>]*\1)[^>]*>/gi,
    (match) => {
      // If the <a> tag already has a target attribute, leave it unchanged
      if (match.includes('target=')) {
        return match;
      }
      return match.replace('<a ', '<a target="_blank" ');
    }
  );

  const html = DOMPurify.sanitize(formattedHtml, {
    FORCE_BODY: true,
    WHOLE_DOCUMENT: true,
    FORBID_TAGS: ['style'],
    ADD_ATTR: ['target'],
    ...options,
  });
  let doc;
  try {
    const parser = new DOMParser();
    doc = parser.parseFromString(html, 'text/html');
  } catch (e) {
    console.error('DOMParser failed:', e);
    return '';
  }

  return doc && doc.body ? doc.body.innerHTML : '';
};

/**
 * Highlights all occurrences of the given query string inside the provided HTML by wrapping them in <mark> tags.
 * The function parses the HTML, traverses its text nodes, and replaces matching text with <mark> elements.
 * Returns the modified HTML as a string.
 */
export const highlightTextInHTML = (html, query) => {
  if (!isValidQuery(query)) return html;

  let tempElement;
  try {
    tempElement = document.createElement('div');
    tempElement.innerHTML = html;
  } catch (e) {
    console.error('Failed to create temp element or set innerHTML:', e);
    return html;
  }

  const traverseAndHighlight = (node) => {
    try {
      // If the node is a text node and contains the query, highlight it
      if (node.nodeType === 3 && query) {
        const regex = new RegExp('(' + escapeRegExp(query) + ')', 'gi');
        const parts = node.textContent.split(regex);
        if (parts.length > 1) {
          const fragment = document.createDocumentFragment();
          parts.forEach((part, i) => {
            if (i % 2 === 1) {
              // This part matches the query, wrap it in a <mark> tag
              const mark = document.createElement('mark');
              mark.style.padding = '0';
              mark.textContent = part;
              fragment.appendChild(mark);
            } else if (part) {
              fragment.appendChild(document.createTextNode(part));
            }
          });
          // Replace the original text node with the new fragment
          node.parentNode && node.parentNode.replaceChild(fragment, node);
        }
      } else if (node.childNodes) {
        Array.from(node.childNodes).forEach(traverseAndHighlight);
      }
    } catch (e) {
      // Catch and log any DOM errors
      console.error('Error during traverseAndHighlight:', e);
    }
  };

  try {
    traverseAndHighlight(tempElement);
    return tempElement.innerHTML;
  } catch (e) {
    console.error('Error highlighting text in HTML:', e);
    return html;
  }
};

export const parseAndSanitizeHtml = (srcHtml, options = {}, textToHighlight = '') => {
  const sanitizedEmailHtml = sanitizeHtml(srcHtml, options);
  try {
    return parse(
      textToHighlight ? highlightTextInHTML(sanitizedEmailHtml, textToHighlight) : sanitizedEmailHtml
    );
  } catch (e) {
    console.error('Error parsing and sanitizing HTML:', e);
    return null;
  }
};

export const removeDivByClass = (srcHtml, divClass) => {
  let doc;
  try {
    const parser = new DOMParser();
    doc = parser.parseFromString(srcHtml, 'text/html');
  } catch (e) {
    console.error('DOMParser failed:', e);
    return { html: srcHtml, hasDiv: false };
  }
  const div = doc.querySelector(`.${divClass}`);
  let hasDiv = false;

  if (div) {
    div.remove();
    hasDiv = true;
  }

  return {
    html: doc.body.innerHTML,
    hasDiv,
  };
};

export const getTextFromHtml = (html, maxLength = null) => {
  if (!html) {
    return '';
  }

  const options = {
    wordwrap: 0,
    baseElement: 'body',
    ignoreHref: true,
    selectors: [{ selector: 'p', options: { leadingLineBreaks: 1, trailingLineBreaks: 1 } }],
  };

  let text = '';
  try {
    text = htmlToText.convert(html, options);
  } catch (e) {
    console.error('html-to-text conversion failed:', e);
    return '';
  }

  if (!maxLength) {
    return text;
  }

  return getValueWithEllipsis(text, maxLength);
};

export { parse };